/**
 * Twin genealogy resource (M8.1) — typed wrappers over the part-level
 * traceability surface: backward part/lot trace + forward where-used.
 *
 * Wire shapes mirror platform-core/internal/twin/genealogy.go exactly. The
 * `parameters` / `properties` arrays use the same {name,value,unit,range,inSpec}
 * shape the batch trace uses, so the param-chip renderer is shared.
 */

import { api } from "@/lib/api-client";
import { qs } from "./qs";
import type { CastingHoldBadge } from "./casting-holds";

/**
 * One observation a fused property chip was derived from (σ-fusion σF-1..4).
 *
 * Mirrors `reconcile.Observation` (platform-core/internal/reconcile/fuse_property.go)
 * on the wire. This is the AUDIT TRAIL: append-only, de-duplicated server-side by
 * (`method`, `sample_id`), and never mutated — the ESTIMATED library blend stays
 * visible after a METERED OES assay lands beside it and out-weighs it.
 */
export interface TraceObservation {
  value: number;
  sigma: number;
  /** METERED (a real assay) | CALCULATED | ESTIMATED (a library/blend prior). */
  tier: string;
  /** How it was obtained — `library_blend`, `library_charge_component`, `oes`, … */
  method: string;
  /** Stable per-(lot, method, sample) key. Identity, not a measurement. */
  sample_id: string;
  /** RFC3339 as-of time; omitted when the source carried no time. */
  at?: string;
}

/** A captured parameter or measured lot property (in-spec aware). */
export interface TraceParam {
  name: string;
  value: number | string;
  unit?: string;
  range?: [number, number];
  inSpec?: boolean;

  /**
   * σ-fusion (σF-1..4) — present ONLY on chemistry element chips (C/Si/Mn/S/P/Mg)
   * and the derived channels (CE, liquidus_c). Mirrors `paramChip` in
   * platform-core/internal/twin/genealogy_emit.go, where every one of these is
   * `omitempty` — so every NON-chem chip arrives byte-unchanged and these stay
   * undefined.
   *
   * `value`/`sigma`/`tier` are the DERIVED projection over `observations`
   * (reconcile.FuseProperty); `observations` is the trail it was projected from.
   * A fusable chip that carries `observations` but NO `tier` is WITHHELD — the
   * fusion produced nothing, and its `value` is a Go zero, not a measurement.
   * See foundry-widgets/chemistry.ts, which is the one place that is decided.
   */
  sigma?: number;
  tier?: string;
  basis?: string;
  observations?: TraceObservation[];
}

/** A lot as it appears on an operation edge. */
export interface TraceLot {
  id: string;
  material_class: string;
  /**
   * The lot's QUANTITY in `quantity_unit` — usually a PIECE COUNT, not a mass.
   * It is NOT NULL DEFAULT 0 server-side, so it can never express "unknown".
   * Do not read it as kilograms; see `mass_kg`.
   */
  quantity_value: number;
  quantity_unit: string;
  port: string;
  properties: TraceParam[];

  /**
   * The lot's MASS in kilograms, or `null` when nobody measured it (S1.1).
   *
   * `null` and `0` are different facts and must render differently: a lot that
   * was never weighed shows "— kg (not measured)", never a confident "0 kg".
   * Optional so an older API response degrades to unknown rather than zero.
   */
  mass_kg?: number | null;
  /** METERED | CALCULATED | ESTIMATED — how the mass was arrived at. */
  mass_tier?: string;
  /** Which store the value came from (fused chip, column, legacy kg). */
  mass_source?: string;

  /**
   * The identity grain this lot is tracked at (M4 / S2.4): `unit` (per-piece
   * ids), `line_shift`, or `bulk_lot` (heat-grain). Lets the trace say "grouped to
   * line/shift — per-casting detail not tracked at this plant" instead of implying
   * a precision the plant does not have. Absent on legacy rows → no label.
   */
  resolution_tier?: string;
}

export type LotOperationType = "TRANSFORM" | "SPLIT" | "MERGE" | "PASSTHROUGH";

export interface TraceOperation {
  id: string;
  operation_type: LotOperationType;
  entity: string;
  started_at: string | null;
  ended_at: string | null;
  input_lots: TraceLot[];
  output_lots: TraceLot[];
  parameters: TraceParam[];
  out_of_spec: boolean;
  /**
   * Wave A / A4 — the heat's JOB IDENTITY (product · recipe · tooling). The three
   * ids ride `lot_operations` (migration 0121); `product_name` / `pattern_code` are
   * name-resolved server-side so the trace head shows a readable label, not a raw id.
   * Heat-wide — identical on every op of a run — so the UI reads them once at the
   * trace head. Every field is omitted when the run declared no identity (no-recipe /
   * import op): the honest absence, never a fabricated id.
   */
  product_id?: string;
  recipe_id?: string;
  pattern_id?: string;
  product_name?: string;
  pattern_code?: string;
  /**
   * The entity INSTANCE that ran this operation (material-genealogy-real C4).
   * Optional + additive. **`entity_instance_id` === `PlantNode.id`** — the
   * single join key the process-graph overlay + trace-path highlight use to
   * map an operation back to a node on the canvas. Mirrors
   * `lot_operations.entity_instance_id` (already in the schema, no migration).
   */
  entity_instance_id?: string;
  /**
   * Which physical lane this operation ran on (material-genealogy-real C4) —
   * the specific furnace / pour-line in a multi-furnace, multi-line plant.
   * Optional + additive; rendered as a lane chip and used to scope the
   * path highlight.
   */
  lane?: { furnace_id?: string; line_id?: string };
}

export interface TracePartHead {
  id: string;
  heat_id: string | null;
  grade: string;
  /** Stage of this output — raw_casting / green_sand_mould / test_specimen. */
  material_class: string;
  status: string;
  defect_type: string | null;
  defect_severity: string | null;
  lot_id: string;
  /**
   * This unit's own as-built conditions (M8.9.2 per-unit variation): e.g. a
   * casting's pour sequence / pour temperature / cooling time, or a mould's
   * sand_moisture — distinct per unit even within one heat. Same shape as
   * operation parameters.
   */
  properties?: TraceParam[];
}

export interface TraceSummary {
  operations: number;
  first_out_of_spec_op_id: string;
}

export interface PartTrace {
  /** Head when the trace was entered from a UNIT (CA-/MO-/TS-). */
  part?: TracePartHead;
  /** Head when the trace was entered from a LOT (L-…-sand, L-…-molten, …). */
  lot?: Lot;
  /** "Where from" (root cause) — backward lineage: raw materials → this output. */
  operations: TraceOperation[];
  /** "Where used" (recall) — forward lineage: this output → everything downstream. May be []. */
  forward_operations: TraceOperation[];
  summary: TraceSummary;
  /**
   * The real route this output took through the plant (material-genealogy-real
   * C4) — the ordered graph nodes + edges it actually traversed, plus the lane
   * it ran on. Optional + additive: present only for new-model (genealogy_v2)
   * traces; legacy traces omit it and the "Show on graph" affordance hides.
   *
   * `node_ids` are **`PlantNode.id`** values (== operation `entity_instance_id`),
   * the join key for the canvas highlight. `edge_keys` are the traversed
   * `PlantEdge.id`s; `furnace_id` / `line_id` name the lane for the lane chip.
   */
  path?: {
    node_ids: string[];
    edge_keys?: string[];
    furnace_id?: string;
    line_id?: string;
  };
}

/**
 * A single tracked output unit at any foundry stage — a casting (CA-…), a
 * mould (MO-…), or a test specimen (TS-…). The `material_class` distinguishes
 * the stage; `defect_type`/`defect_severity` are stage-appropriate (e.g. a
 * mould fails `sand_inclusion`/`low_hardness`, a specimen `low_strength`).
 */
export interface TrackedUnit {
  id: string;
  org_id: string;
  lot_id: string;
  heat_id: string | null;
  grade: string;
  /** Stage of this output — raw_casting / green_sand_mould / test_specimen. */
  material_class: string;
  status: string;
  defect_type: string | null;
  defect_severity: string | null;
  produced_at: string;
  properties: TraceParam[];
  /**
   * Where the unit came from in the tooling, and which mould poured it. All
   * optional — a legacy or non-cast unit carries none of them.
   *
   * pattern_id/cavity_no also appear as `properties` chips, but lossily (the id
   * rides in the chip's `unit` field because a chip value is a number), and
   * mould_id is not chipped at all. Read these.
   */
  pattern_id?: string;
  cavity_no?: number;
  sublot_id?: string;
  mould_id?: string;
  /** The casting holds its heat is in, open first, on the units list (step 6); absent from an older API. */
  casting_holds?: CastingHoldBadge[];
}

/**
 * Back-compat alias: this surface was casting-only before P1.5 generalized it
 * to the output-centric model. Existing imports of `CastingUnit` keep working.
 */
export type CastingUnit = TrackedUnit;

export interface BatchParts {
  items: TrackedUnit[];
  /** Bulk stages (charge/sand/molten) return per-heat LOTS here instead of unit items. */
  lots?: Lot[];
  /** Representation kind from the backend: discrete_part (items) vs bulk_lot (lots). */
  kind?: "discrete_part" | "bulk_lot";
  total: number;
  failed: number;
  by_defect: Record<string, number>;
  limit: number;
  offset: number;
}

/** A foundry output stage — the unit-stage selector on the Parts screen. */
export interface OutputStage {
  /** material_class filter value sent to the API. */
  materialClass: string;
  /** Tab label. */
  label: string;
  /** Singular noun for a single unit's head (e.g. "Mould"). */
  noun: string;
  /** Expected id prefix, for the "trace any id" hint. */
  idPrefix: string;
  /** discrete_part → unit table; bulk_lot → per-heat lot table. */
  kind: "discrete_part" | "bulk_lot";
}

/**
 * The three unit-bearing stages, in process order. Bulk outputs (the sand
 * batch `green_sand`, the heat `molten_iron`) are LOTS, not units, so they
 * are reachable only via the "trace any id" box — not listed here.
 */
export const OUTPUT_STAGES: readonly OutputStage[] = [
  { materialClass: "raw_casting", label: "Castings", noun: "Casting", idPrefix: "CA-", kind: "discrete_part" },
  { materialClass: "green_sand_mould", label: "Moulds", noun: "Mould", idPrefix: "MO-", kind: "discrete_part" },
  { materialClass: "test_specimen", label: "Specimens", noun: "Specimen", idPrefix: "TS-", kind: "discrete_part" },
] as const;

/** Human label for a material_class (falls back to a de-underscored form). */
export function materialClassNoun(materialClass: string): string {
  return (
    OUTPUT_STAGES.find((s) => s.materialClass === materialClass)?.noun ??
    materialClass.replace(/_/g, " ")
  );
}

export type LotStatus =
  | "active"
  | "consumed"
  | "partial"
  | "expired"
  | "scrapped";

export type FlowMode = "batch" | "continuous" | "discrete_unit";

/** A full material lot (mirrors genealogy.go Lot). */
export interface Lot {
  id: string;
  org_id: string;
  lot_number: string;
  material_class: string;
  heat_id: string | null;
  quantity_value: number;
  quantity_unit: string;
  status: string;
  created_by_entity: string;
  source_lot_ids: string[];
  properties: TraceParam[];
  created_at: string;
  updated_at: string;
}

/**
 * Body of POST /v1/twin/lots (M8.8 ingestion contract). `id` is the
 * client-authored TEXT lot id (e.g. "L-04821-molten"); the write is idempotent
 * on it. Mirrors genealogy_write.go lotReq (snake_case).
 */
export interface LotCreate {
  id: string;
  material_class: string;
  lot_number?: string;
  heat_id?: string;
  quantity_value?: number;
  /** Defaults to "kg" server-side. */
  quantity_unit?: string;
  /** Defaults to "active" server-side. */
  status?: LotStatus;
  created_by_entity?: string;
  source_lot_ids?: string[];
  properties?: TraceParam[];
}

/** One input/output lot edge on an operation write. */
export interface OperationEdge {
  lot_id: string;
  quantity_value?: number;
  quantity_unit?: string;
  port?: string;
}

/**
 * Body of POST /v1/twin/operations (M8.8 ingestion contract). One tx upserts
 * the operation + its input/output edges; idempotent on the client-authored
 * `id`. Mirrors genealogy_write.go operationReq (snake_case).
 */
export interface OperationCreate {
  id: string;
  operation_type: LotOperationType;
  entity_instance_id?: string;
  heat_id?: string;
  started_at?: string;
  ended_at?: string;
  input_qty?: number;
  output_qty?: number;
  throughput_rate?: number;
  flow_mode?: FlowMode;
  process_parameters?: TraceParam[];
  inputs?: OperationEdge[];
  outputs?: OperationEdge[];
}

/** 201 response of POST /v1/twin/operations (mirrors genealogy_write.go OperationCreated). */
export interface OperationCreated {
  id: string;
  org_id: string;
  operation_type: LotOperationType;
  entity: string;
  heat_id: string | null;
  started_at: string | null;
  ended_at: string | null;
  input_qty: number | null;
  output_qty: number | null;
  throughput_rate: number | null;
  flow_mode: FlowMode | null;
  parameters: TraceParam[];
  input_lots: TraceLot[];
  output_lots: TraceLot[];
}

/**
 * One member reading of a heat (versioned reading↔heat membership, migration
 * 0104). `value`/`value_text` are joined from the immutable readings row where
 * it still exists (null if purged); `version`/`source` describe the membership
 * ASSERTION, not the reading — `source` is `capture` (attested at assembly),
 * `reassignment` (a human moved/attached it), or `backfill_inferred`.
 */
export interface HeatReadingMember {
  device_id: string;
  metric: string;
  reading_time: string;
  genealogy_role: string;
  heat_id: string;
  version: number;
  source: string;
  value: number | null;
  value_text: string | null;
}

/**
 * A heat's versioned membership view (GET /v1/twin/batches/{id}/readings).
 * `origin` "capture" = assembled from floor readings, so an empty `readings` is
 * a real gap; "non_capture" = generated/imported, which HAS no source readings,
 * so empty is the CORRECT answer, not a gap. `membership_recorded` separates
 * "captured, none recorded yet (pre-ledger)" from "captured, N recorded".
 */
export interface HeatReadings {
  heat_id: string;
  origin: "capture" | "non_capture";
  membership_recorded: boolean;
  as_of: string;
  readings: HeatReadingMember[];
}

/** A reading's natural key (readings has no surrogate id). */
export interface ReadingKey {
  device_id: string;
  metric: string;
  reading_time: string;
}

/** Body of POST /v1/twin/reading-associations:reassign. Provide readings[] OR from_heat_id. */
export interface ReassignBody {
  to_heat_id: string;
  from_heat_id?: string;
  readings?: ReadingKey[];
  reason?: string;
}

export interface ReassignMove {
  device_id: string;
  metric: string;
  reading_time: string;
  from_heat_id: string | null;
  to_heat_id: string;
  from_version: number | null;
  to_version: number;
  outcome: "moved" | "assigned" | "noop";
}

export interface ReassignResult {
  to_heat_id: string;
  moved: number;
  moves: ReassignMove[];
  /** True after any move: the derived genealogy DAG still reflects the old membership. */
  regenerate_pending: boolean;
}

export const twinGenealogy = {
  /** Backward + forward lineage of one unit (part/lot heads + both directions). */
  partTrace: (partId: string) =>
    api.get<PartTrace>(`/v1/twin/parts/${encodeURIComponent(partId)}/trace`),
  /** Backward + forward lineage of one lot. */
  lotTrace: (lotId: string) =>
    api.get<PartTrace>(`/v1/twin/lots/${encodeURIComponent(lotId)}/trace`),
  /**
   * Universal trace entry: `id` may be a unit id (CA-/MO-/TS-…) OR a lot id
   * (L-…-sand, L-…-molten, …). Resolves the head (part OR lot), the backward
   * `operations`, and the forward `forward_operations`. 404 `output_not_found`
   * when the id matches no tracked output.
   */
  outputTrace: (id: string) =>
    api.get<PartTrace>(`/v1/twin/trace/${encodeURIComponent(id)}`),
  /**
   * Tracked units across the org for a stage. Every filter is optional
   * (empty = no filter); `material_class` selects the stage (raw_casting /
   * green_sand_mould / test_specimen).
   */
  units: (params?: {
    material_class?: string;
    heat_id?: string;
    status?: string;
    grade?: string;
    limit?: number;
    offset?: number;
  }) =>
    api.get<{
      items: TrackedUnit[];
      total: number;
      limit: number;
      offset: number;
    }>(`/v1/twin/units${qs(params)}`),
  /**
   * Forward where-used — the units a heat produced (+ aggregate header).
   * `material_class` defaults to `raw_casting` server-side; pass it to view a
   * heat's moulds or specimens instead of its castings.
   */
  batchParts: (
    heatId: string,
    params?: {
      material_class?: string;
      limit?: number;
      offset?: number;
    },
  ) =>
    api.get<BatchParts>(
      `/v1/twin/batches/${encodeURIComponent(heatId)}/parts${qs(params)}`,
    ),
  /**
   * Upsert a material lot (M8.8 write contract). Idempotent on `body.id`; the
   * single write path the live worker / MES / manual entry share.
   */
  createLot: (body: LotCreate) => api.post<Lot>("/v1/twin/lots", body),
  /**
   * Upsert an operation + its input/output edges in one tx (M8.8 write
   * contract). Idempotent on `body.id`.
   */
  createOperation: (body: OperationCreate) =>
    api.post<OperationCreated>("/v1/twin/operations", body),
  /**
   * A heat's versioned source-reading MEMBERSHIP (migration 0104), resolved as
   * of `asOf` (RFC3339) for reproducibility — omit for "now" (current membership).
   */
  heatReadings: (heatId: string, asOf?: string) =>
    api.get<HeatReadings>(
      `/v1/twin/batches/${encodeURIComponent(heatId)}/readings${qs({ as_of: asOf })}`,
    ),
  /**
   * Move readings between heats (or attach an unassociated one) — append-only,
   * audited. The write side of the versioned-membership model (#197). Returns
   * the per-reading outcome + `regenerate_pending`.
   */
  reassignReadingAssociations: (body: ReassignBody) =>
    api.post<ReassignResult>("/v1/twin/reading-associations:reassign", body),
};
